import React, { useState } from "react";
import { Music, Users, Star, CheckCircle, Clock, Bell } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const JoinWaitlist: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    interest: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    toast.success("Thank you for joining our waitlist! We'll be in touch soon.");
    setFormData({
      name: '',
      email: '',
      company: '',
      role: '',
      interest: '',
      message: ''
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const benefits = [
    {
      icon: <Star className="w-6 h-6" />,
      title: "Early Access",
      description: "Be among the first to experience our revolutionary AI music classification framework"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Exclusive Community",
      description: "Join industry leaders and innovators shaping the future of AI in music"
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Priority Updates",
      description: "Receive priority notifications about feature releases and platform updates"
    }
  ];

  const waitlistStats = [
    { number: "20+", label: "Organizations Interested" },
    { number: "10+", label: "Non-Profits Engaged" },
    { number: "Q4 2025", label: "Expected Launch" }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section with tagblue background */}
      <div className="bg-gradient-to-b from-tagblue to-tagblue/80 relative overflow-hidden">
        {/* 3D Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute top-1/3 right-20 w-96 h-96 bg-tagblue-light/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }}></div>
          
          {/* Geometric shapes */}
          <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-white/30 rotate-45 animate-pulse"></div>
          <div className="absolute top-3/4 right-1/3 w-6 h-6 bg-tagblue-light/40 rotate-12 animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-1/3 left-1/2 w-3 h-3 bg-white/20 rounded-full animate-pulse" style={{ animationDelay: '3s' }}></div>
        </div>

        <Navbar />
        
        {/* Hero Content */}
        <section className="relative pt-16 pb-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <div className="mb-8 animate-slide-up">
                <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                  Join the
                  <span className="block bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
                    Waitlist
                  </span>
                </h1>
                <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                  Be part of the revolution in AI music classification. Get early access to tools that will transform how the industry handles AI-generated content.
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                {waitlistStats.map((stat, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                    <div className="text-3xl font-bold text-white mb-2">{stat.number}</div>
                    <div className="text-white/80">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Main Content Section with white background */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start max-w-7xl mx-auto">
            
            {/* Benefits Section */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Join Our Waitlist?</h2>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Be at the forefront of the AI music revolution. Our platform provides unprecedented 
                  transparency and control over AI usage in music production.
                </p>
              </div>

              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 hover:border-tagblue/30 transition-all duration-300 shadow-sm hover:shadow-md">
                    <div className="flex items-start space-x-4">
                      <div className="text-tagblue bg-tagblue/10 p-3 rounded-lg">
                        {benefit.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                        <p className="text-gray-600">{benefit.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-tagblue/5 to-tagblue/10 rounded-xl p-6 border border-tagblue/20">
                <div className="flex items-center space-x-3 mb-4">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <h3 className="text-xl font-semibold text-gray-900">What to Expect</h3>
                </div>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center"><span className="w-2 h-2 bg-tagblue rounded-full mr-3"></span>Early access invitations</li>
                  <li className="flex items-center"><span className="w-2 h-2 bg-tagblue rounded-full mr-3"></span>Exclusive beta testing opportunities</li>
                  <li className="flex items-center"><span className="w-2 h-2 bg-tagblue rounded-full mr-3"></span>Direct feedback channels with our team</li>
                  <li className="flex items-center"><span className="w-2 h-2 bg-tagblue rounded-full mr-3"></span>Priority customer support</li>
                </ul>
              </div>
            </div>

            {/* Form Section */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 shadow-lg">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Reserve Your Spot</h2>
                <p className="text-gray-600">Fill out the form below to join our exclusive waitlist</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 mb-2 block font-medium">Full Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-tagblue focus:ring-tagblue/20"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-700 mb-2 block font-medium">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-tagblue focus:ring-tagblue/20"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company" className="text-gray-700 mb-2 block font-medium">Company/Organization</Label>
                    <Input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-tagblue focus:ring-tagblue/20"
                      placeholder="Enter your company name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="role" className="text-gray-700 mb-2 block font-medium">Your Role</Label>
                    <Select value={formData.role} onValueChange={(value) => handleInputChange('role', value)}>
                      <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="artist">Artist/Musician</SelectItem>
                        <SelectItem value="producer">Producer</SelectItem>
                        <SelectItem value="label">Record Label</SelectItem>
                        <SelectItem value="platform">Platform/Service</SelectItem>
                        <SelectItem value="legal">Legal Professional</SelectItem>
                        <SelectItem value="researcher">Researcher</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="interest" className="text-gray-700 mb-2 block font-medium">Primary Interest</Label>
                  <Select value={formData.interest} onValueChange={(value) => handleInputChange('interest', value)}>
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                      <SelectValue placeholder="What interests you most?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="copyrightability">Copyrightability Engine</SelectItem>
                      <SelectItem value="framework">AI Music Framework</SelectItem>
                      <SelectItem value="compliance">Industry Compliance</SelectItem>
                      <SelectItem value="classification">Music Classification</SelectItem>
                      <SelectItem value="all">All Features</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-gray-700 mb-2 block font-medium">Additional Information</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-tagblue focus:ring-tagblue/20"
                    placeholder="Tell us about your specific needs or questions..."
                    rows={4}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-tagblue text-white hover:bg-tagblue-dark transition-all duration-300 py-6 text-lg font-semibold shadow-lg hover:shadow-xl"
                >
                  <Clock className="w-5 h-5 mr-2" />
                  Join the Waitlist
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-500 text-sm">
                  By joining, you agree to receive updates about our platform. 
                  You can unsubscribe at any time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JoinWaitlist;